import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { CheckCircle, CreditCard, Truck, Shield, Lock, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFirstImage = (product) => {
  if (!product || !product.images) return '';
  return product.images.length > 0 ? product.images[0] : '';
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, cartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  // Check for returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setOrderComplete(true);
        setOrderId(data.order_id);
        await clearCart();
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error('Error verifying payment. Please contact support.');
        setCheckingPayment(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // First create the order
      const orderItems = items.map(item => ({
        product_id: item.product_id,
        product_name: item.product?.name || 'Product',
        price: item.product?.sale_price || item.product?.price || 0,
        quantity: item.quantity
      }));

      const shipping = cartTotal >= 2500 ? 0 : 149.99;
      const total = cartTotal + shipping;

      const orderData = {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        shipping_address: shippingAddress,
        city,
        state,
        zip_code: zipCode,
        items: orderItems,
        subtotal: cartTotal,
        shipping,
        total
      };

      // Create order first
      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const createdOrderId = orderResponse.data.id;

      // Create Stripe checkout session
      const checkoutResponse = await axios.post(`${API}/checkout/create-session`, {
        order_id: createdOrderId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Error processing checkout', {
        description: 'Please try again.'
      });
      setLoading(false);
    }
  };

  // Show loading while checking payment
  if (checkingPayment) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <Loader2 size={48} className="mx-auto text-[#8B5E3C] animate-spin mb-6" />
            <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-4">Verifying Payment...</h2>
            <p className="text-[#6B7280]">Please wait while we confirm your payment.</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isEmpty && !orderComplete && !searchParams.get('session_id')) {
    navigate('/cart');
    return null;
  }

  if (orderComplete) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="order-success">
          <div className="max-w-md text-center animate-scale-in">
            <CheckCircle size={80} className="mx-auto text-[#10B981] mb-6" />
            <h1 className="font-manrope text-3xl font-bold text-[#1A1A1A] mb-4">Payment Successful!</h1>
            <p className="text-[#6B7280] mb-2">Thank you for your order. A confirmation has been sent to your email.</p>
            {orderId && <p className="text-sm text-[#9CA3AF] mb-8">Order ID: <span className="font-mono">{orderId}</span></p>}
            <div className="space-y-3">
              <Button asChild className="w-full"><a href="/shop">Continue Shopping</a></Button>
              <Button variant="outline" asChild className="w-full"><a href="/">Return to Home</a></Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const shipping = cartTotal >= 2500 ? 0 : 149.99;
  const total = cartTotal + shipping;

  const renderSummaryItem = (item) => {
    const imgSrc = getFirstImage(item.product);
    const price = item.product?.sale_price || item.product?.price || 0;
    const itemTotal = price * item.quantity;

    return (
      <div key={item.product_id} className="flex gap-4">
        <div className="w-16 h-16 bg-white flex-shrink-0">
          {imgSrc && <img src={imgSrc} alt={item.product?.name || ''} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-[#1A1A1A] truncate">{item.product?.name}</p>
          <p className="text-xs text-[#6B7280]">Qty: {item.quantity}</p>
        </div>
        <div className="text-sm font-medium">${itemTotal.toFixed(2)}</div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="checkout-page">
        <h1 className="font-manrope text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="checkout-form">
              <div>
                <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">Contact Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="form-label">Full Name *</label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} required data-testid="checkout-name-input" /></div>
                  <div><label className="form-label">Email Address *</label><Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required data-testid="checkout-email-input" /></div>
                  <div className="sm:col-span-2"><label className="form-label">Phone Number *</label><Input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} required data-testid="checkout-phone-input" /></div>
                </div>
              </div>

              <div>
                <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div><label className="form-label">Street Address *</label><Input value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} required data-testid="checkout-address-input" /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div><label className="form-label">City *</label><Input value={city} onChange={(e) => setCity(e.target.value)} required data-testid="checkout-city-input" /></div>
                    <div><label className="form-label">Province *</label><Input value={state} onChange={(e) => setState(e.target.value)} required data-testid="checkout-state-input" /></div>
                    <div className="col-span-2 sm:col-span-1"><label className="form-label">Postal Code *</label><Input value={zipCode} onChange={(e) => setZipCode(e.target.value)} required data-testid="checkout-zip-input" /></div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-4">Payment</h2>
                <div className="bg-[#F9F7F2] p-6 border border-[#E5E7EB]">
                  <div className="flex items-center gap-3 mb-4"><CreditCard size={24} className="text-[#8B5E3C]" /><span className="font-medium">Secure Payment via Stripe</span></div>
                  <p className="text-sm text-[#6B7280]">You will be redirected to Stripe's secure checkout page to complete your payment.</p>
                  <div className="flex items-center gap-2 mt-4 text-sm text-[#6B7280]"><Lock size={14} /><span>256-bit SSL encrypted payment</span></div>
                </div>
              </div>

              <Button type="submit" size="lg" className="w-full bg-[#1A1A1A] hover:bg-[#333333]" disabled={loading} data-testid="place-order-btn">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    Redirecting to payment...
                  </span>
                ) : (
                  `Proceed to Payment - $${total.toFixed(2)} CAD`
                )}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#F9F7F2] p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="font-manrope text-xl font-semibold text-[#1A1A1A] mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">{items.map(renderSummaryItem)}</div>
              <div className="space-y-3 pt-4 border-t border-[#E5E7EB]">
                <div className="flex justify-between text-[#6B7280]"><span>Subtotal</span><span>${cartTotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-[#6B7280]"><span>Shipping</span><span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span></div>
                <div className="flex justify-between text-lg font-semibold text-[#1A1A1A] pt-3 border-t border-[#E5E7EB]"><span>Total</span><span>${total.toFixed(2)} CAD</span></div>
              </div>
              <div className="mt-6 pt-6 border-t border-[#E5E7EB] space-y-3">
                <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Truck size={16} className="text-[#8B5E3C]" /><span>{shipping === 0 ? 'Free shipping included' : 'Flat rate shipping'}</span></div>
                <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Shield size={16} className="text-[#8B5E3C]" /><span>5-year warranty on all cabinets</span></div>
                <div className="flex items-center gap-2 text-sm text-[#6B7280]"><Lock size={16} className="text-[#8B5E3C]" /><span>Secure Stripe checkout</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
